import { useEffect, useRef, useState } from "react";

// Keeps a live HTTP/Icecast stream playing until the user presses stop:
// - reconnects on error / ended / stalled, and when audio stops advancing (watchdog),
// - exponential backoff 1s → 30s, rotating through fallback stream URLs,
// - resumes instantly when the network comes back,
// - Media Session API → lock-screen / notification controls and background playback on phones.
export function useResilientAudio({ audioRef, playing, urls, onFatal, onUserPause, meta, setLoading }) {
  const [status, setStatus] = useState("idle");
  const st = useRef({ attempt: 0, idx: 0, timer: null, lastT: 0, lastMove: 0, wantPlay: false });
  const urlsKey = urls.join("|");
  const urlsRef = useRef(urls);
  urlsRef.current = urls;

  useEffect(() => {
    const a = audioRef.current;
    const s = st.current;
    if (!a) return undefined;
    const clear = () => { if (s.timer) clearTimeout(s.timer); s.timer = null; };
    const start = (fresh) => {
      clear();
      const list = urlsRef.current;
      if (!list.length) return;
      const base = list[s.idx % list.length];
      // Cache-buster forces a brand-new connection (a stale socket is the usual reason a stream "dies").
      a.dataset.ownPause = "1";
      a.src = fresh ? base : `${base}${base.includes("?") ? "&" : "?"}_r=${Date.now()}`;
      setLoading(true);
      a.play().then(() => { setLoading(false); }).catch((e) => {
        setLoading(false);
        if (e?.name === "NotAllowedError") { setStatus("blocked"); return; }
        retry();
      });
    };
    const retry = () => {
      if (!s.wantPlay) return;
      clear();
      if (navigator.onLine === false) { setStatus("offline"); return; }
      s.attempt += 1;
      if (s.attempt % 3 === 0) { s.idx += 1; if (onFatal) onFatal(); }  // rotate to a fallback stream / refresh URL
      const delay = Math.min(30000, 1000 * 2 ** Math.min(s.attempt - 1, 5));
      setStatus("reconnecting");
      s.timer = setTimeout(() => start(false), delay);
    };
    const onPlaying = () => { a.dataset.ownPause = ""; s.attempt = 0; s.lastMove = Date.now(); setStatus("live"); setLoading(false); };
    const onTime = () => { if (a.currentTime !== s.lastT) { s.lastT = a.currentTime; s.lastMove = Date.now(); } };
    const onBad = () => { if (s.wantPlay) retry(); };
    const onWaiting = () => setLoading(true);
    const onPause = () => {
      // Paused by the OS / headset / lock screen (not by our Stop button) → reflect it in the UI.
      if (a.dataset.ownPause === "1") { a.dataset.ownPause = ""; return; }
      if (s.wantPlay && !a.ended && a.error == null && document.visibilityState === "visible" && onUserPause) onUserPause();
    };
    const onOnline = () => { if (s.wantPlay) { s.attempt = 0; start(false); } };
    const onOffline = () => { if (s.wantPlay) setStatus("offline"); };
    window.addEventListener("offline", onOffline);
    a.addEventListener("playing", onPlaying);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("error", onBad);
    a.addEventListener("ended", onBad);
    a.addEventListener("stalled", onBad);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("pause", onPause);
    window.addEventListener("online", onOnline);
    const watchdog = setInterval(() => {
      if (s.wantPlay && !a.paused && Date.now() - s.lastMove > 15000) retry();  // silent stall
    }, 5000);
    return () => {
      clearInterval(watchdog); clear();
      a.removeEventListener("playing", onPlaying); a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("error", onBad); a.removeEventListener("ended", onBad); a.removeEventListener("stalled", onBad);
      a.removeEventListener("waiting", onWaiting); a.removeEventListener("pause", onPause);
      window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline);
    };
  }, [audioRef, onFatal, onUserPause, setLoading]);

  useEffect(() => {
    const a = audioRef.current;
    const s = st.current;
    if (!a) return;
    s.wantPlay = playing && urls.length > 0;
    if (s.wantPlay) {
      s.attempt = 0; s.lastMove = Date.now();
      if (a.paused || !a.src) {
        const base = urls[s.idx % urls.length];
        a.dataset.ownPause = "1";
        a.src = base; setLoading(true);
        a.play().then(() => setLoading(false)).catch(() => { setLoading(false); a.dispatchEvent(new Event("error")); });
      }
    } else {
      if (s.timer) clearTimeout(s.timer);
      a.dataset.ownPause = "1";
      a.pause();
      a.removeAttribute("src"); a.load();  // drop the connection so we don't buffer data in the background
      setStatus("idle");
    }
  }, [playing, urlsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: meta?.showTitle || "B&B KIGALI 89.7 FM", artist: meta?.djName || "Live radio", album: "B&B KIGALI FM",
        artwork: [{ src: meta?.coverImage || "/bb-logo.png", sizes: "512x512", type: "image/png" }],
      });
      navigator.mediaSession.playbackState = playing ? "playing" : "paused";
      navigator.mediaSession.setActionHandler("play", () => { audioRef.current?.play().catch(() => {}); });
      navigator.mediaSession.setActionHandler("pause", () => onUserPause && onUserPause());
      navigator.mediaSession.setActionHandler("stop", () => onUserPause && onUserPause());
    } catch { /* older browsers */ }
  }, [meta, playing, audioRef, onUserPause]);

  return { status };
}
